import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, BarChart3, Eye, Download, ChevronDown, ChevronUp, Trash2, Settings, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Admin() {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({ advanced_session_days: 40 });
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions', 'users', 'settings'
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const [sessionsRes, statsRes, usersRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (sessionsRes.ok) setSessions(await sessionsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (settingsRes.ok) setGlobalSettings(await settingsRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Sesión eliminada');
        setSessions(sessions.filter(s => s.id !== sessionId));
        setDeleteConfirm(null);
      } else {
        toast.error('Error al eliminar sesión');
      }
    } catch (error) {
      toast.error('Error al eliminar sesión');
    }
  };

  const handleToggleCustomSessions = async (userId, currentValue) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/permissions`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ custom_sessions_enabled: !currentValue })
      });
      
      if (response.ok) {
        toast.success(!currentValue ? 'Sesiones personalizadas activadas' : 'Sesiones personalizadas desactivadas');
        setUsers(users.map(u => 
          u.id === userId ? { ...u, custom_sessions_enabled: !currentValue } : u
        ));
      } else {
        toast.error('Error al actualizar permisos');
      }
    } catch (error) {
      toast.error('Error al actualizar permisos');
    }
  };

  const handleUpdateAdvancedDays = async (days) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ advanced_session_days: days })
      });
      
      if (response.ok) {
        toast.success('Configuración actualizada');
        setGlobalSettings({ ...globalSettings, advanced_session_days: days });
        fetchData(); // Refresh users to update their days_until_advanced
      } else {
        toast.error('Error al actualizar configuración');
      }
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case 'hit':
        return <span className="badge-hit px-2 py-1 rounded-full text-xs font-medium">ACIERTO</span>;
      case 'miss':
        return <span className="badge-miss px-2 py-1 rounded-full text-xs font-medium">FALLO</span>;
      default:
        return <span className="badge-pending px-2 py-1 rounded-full text-xs font-medium">PENDIENTE</span>;
    }
  };

  const exportCSV = () => {
    const headers = ['Sesión', 'Usuario', 'Email', 'Activo', 'Precio Base', 'Precio Final', 'Predicción', 'Resultado Real', 'Estado', 'Confianza', 'Fecha'];
    const rows = sessions.map(s => [
      s.session_code,
      s.user_name,
      s.user_email,
      s.symbol,
      s.baseline_price,
      s.verification_price || '',
      s.predicted_dir || '',
      s.actual_dir || '',
      s.result,
      s.confidence || '',
      s.created_at
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'arv_sessions_export.csv';
    a.click();
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 bg-white/5 rounded w-48 mb-8"></div>
        <div className="grid grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Panel de Administración</h1>
          <p className="text-white/50 mt-1">Gestiona sesiones, usuarios y configuración</p>
        </div>
        <button
          onClick={exportCSV}
          data-testid="export-csv-button"
          className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Exportar CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('sessions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'sessions' ? 'bg-[hsl(198,62%,54%)] text-white' : 'bg-white/10 text-white/70 hover:bg-white/15'
          }`}
        >
          <Eye className="w-4 h-4 inline mr-2" />
          Sesiones
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'users' ? 'bg-[hsl(198,62%,54%)] text-white' : 'bg-white/10 text-white/70 hover:bg-white/15'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Usuarios
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'settings' ? 'bg-[hsl(198,62%,54%)] text-white' : 'bg-white/10 text-white/70 hover:bg-white/15'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Configuración
        </button>
      </div>

      {/* Sessions Tab */}
      {activeTab === 'sessions' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="kpi-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(198,62%,54%)]/20 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-[hsl(198,62%,60%)]" />
                  </div>
                  <span className="text-sm text-white/70">Total sesiones</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats.global?.total_sessions || 0}</p>
              </div>

          <div className="kpi-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(160,84%,39%)]/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[hsl(160,84%,50%)]" />
              </div>
              <span className="text-sm text-white/70">Tasa global</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.global?.hit_rate || 0}%</p>
          </div>

          <div className="kpi-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(252,37%,60%)]/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-[hsl(252,37%,70%)]" />
              </div>
              <span className="text-sm text-white/70">Usuarios activos</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats.by_user?.length || 0}</p>
          </div>
        </div>
      )}

      {/* User Breakdown - Part of sessions tab */}
      {activeTab === 'sessions' && stats?.by_user?.length > 0 && (
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Rendimiento por usuario</h2>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="user-stats-table">
              <thead>
                <tr className="text-left text-sm text-white/50 border-b border-[hsl(232,18%,18%)]">
                  <th className="pb-3 font-medium">Usuario</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium">Sesiones</th>
                  <th className="pb-3 font-medium">Aciertos</th>
                  <th className="pb-3 font-medium">Tasa</th>
                  <th className="pb-3 font-medium">Z-score</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_user.map((user) => (
                  <tr key={user.user_id} className="border-b border-[hsl(232,18%,18%)]">
                    <td className="py-3 text-white">{user.user_name}</td>
                    <td className="py-3 text-white/70">{user.email}</td>
                    <td className="py-3 text-white/70">{user.total_sessions}</td>
                    <td className="py-3 text-white/70">{user.hits}</td>
                    <td className="py-3">
                      <span className={user.hit_rate >= 50 ? 'text-[hsl(160,84%,50%)]' : 'text-[hsl(0,70%,60%)]'}>
                        {user.hit_rate}%
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={user.z_score >= 2 ? 'text-[hsl(160,84%,50%)]' : 'text-white/70'}>
                        {user.z_score > 0 ? '+' : ''}{user.z_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Sessions - Also part of sessions tab */}
      {activeTab === 'sessions' && (
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Todas las sesiones</h2>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-sessions-table">
            <thead>
              <tr className="text-left text-sm text-white/50 border-b border-[hsl(232,18%,18%)]">
                <th className="pb-3 font-medium">Sesión</th>
                <th className="pb-3 font-medium">Usuario</th>
                <th className="pb-3 font-medium">Activo</th>
                <th className="pb-3 font-medium">Predicción</th>
                <th className="pb-3 font-medium">Real</th>
                <th className="pb-3 font-medium">Resultado</th>
                <th className="pb-3 font-medium">Fecha</th>
                <th className="pb-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <React.Fragment key={session.id}>
                  <tr 
                    className="border-b border-[hsl(232,18%,18%)] hover:bg-white/5 cursor-pointer"
                    onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                    data-testid="admin-session-row"
                  >
                    <td className="py-3 text-white font-medium">{session.session_code}</td>
                    <td className="py-3 text-white/70">{session.user_name}</td>
                    <td className="py-3 text-white/70">{session.symbol}</td>
                    <td className="py-3">
                      {session.predicted_dir ? (
                        <span className={session.predicted_dir === 'UP' ? 'text-[hsl(160,84%,50%)]' : 'text-[hsl(0,70%,60%)]'}>
                          {session.predicted_dir === 'UP' ? '↑' : '↓'}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-3">
                      {session.actual_dir ? (
                        <span className={session.actual_dir === 'UP' ? 'text-[hsl(160,84%,50%)]' : 'text-[hsl(0,70%,60%)]'}>
                          {session.actual_dir === 'UP' ? '↑' : '↓'}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-3">{getResultBadge(session.result)}</td>
                    <td className="py-3 text-white/50 text-sm">
                      {new Date(session.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="py-3">
                      {expandedSession === session.id ? (
                        <ChevronUp className="w-4 h-4 text-white/50" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white/50" />
                      )}
                    </td>
                  </tr>
                  {expandedSession === session.id && (
                    <tr className="bg-[hsl(232,36%,11%)]">
                      <td colSpan="8" className="p-4">
                        <div className="grid md:grid-cols-4 gap-4 text-sm">
                          {/* Impresiones y boceto */}
                          <div>
                            <p className="text-white/50 mb-2">Impresiones del viewer:</p>
                            <p className="text-white bg-[hsl(232,20%,14%)] p-3 rounded-lg mb-3 min-h-[80px]">
                              {session.viewer_note || 'Sin impresiones registradas'}
                            </p>
                            {session.viewer_sketch_url && (
                              <div>
                                <p className="text-white/50 mb-2">Boceto/Dibujo:</p>
                                <img 
                                  src={session.viewer_sketch_url.startsWith('http') ? session.viewer_sketch_url : `${API_URL}${session.viewer_sketch_url}`}
                                  alt="Boceto"
                                  className="w-full max-w-[200px] rounded-lg border border-[hsl(232,18%,18%)]"
                                />
                              </div>
                            )}
                          </div>
                          
                          {/* Ambas imágenes objetivo */}
                          <div>
                            <p className="text-white/50 mb-2">Objetivo A:</p>
                            {session.image_a_url ? (
                              <div className="relative">
                                <img 
                                  src={session.image_a_url.startsWith('http') ? session.image_a_url : `${API_URL}${session.image_a_url}`}
                                  alt="Objetivo A"
                                  className={`w-full max-w-[150px] rounded-lg border-2 ${
                                    session.viewer_pick === 'A' 
                                      ? 'border-[hsl(198,62%,54%)]' 
                                      : 'border-[hsl(232,18%,18%)]'
                                  }`}
                                />
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {session.viewer_pick === 'A' && (
                                    <span className="px-2 py-0.5 bg-[hsl(198,62%,54%)]/20 text-[hsl(198,62%,60%)] text-xs rounded-full">
                                      Elegida
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    session.up_image_is === 'A'
                                      ? 'bg-[hsl(160,84%,39%)]/20 text-[hsl(160,84%,50%)]'
                                      : 'bg-[hsl(0,70%,50%)]/20 text-[hsl(0,70%,60%)]'
                                  }`}>
                                    {session.up_image_is === 'A' ? '↑ SUBIDA' : '↓ BAJADA'}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full max-w-[150px] h-24 bg-[hsl(232,20%,14%)] rounded-lg flex items-center justify-center text-white/30">
                                Sin imagen
                              </div>
                            )}
                          </div>

                          <div>
                            <p className="text-white/50 mb-2">Objetivo B:</p>
                            {session.image_b_url ? (
                              <div className="relative">
                                <img 
                                  src={session.image_b_url.startsWith('http') ? session.image_b_url : `${API_URL}${session.image_b_url}`}
                                  alt="Objetivo B"
                                  className={`w-full max-w-[150px] rounded-lg border-2 ${
                                    session.viewer_pick === 'B' 
                                      ? 'border-[hsl(198,62%,54%)]' 
                                      : 'border-[hsl(232,18%,18%)]'
                                  }`}
                                />
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {session.viewer_pick === 'B' && (
                                    <span className="px-2 py-0.5 bg-[hsl(198,62%,54%)]/20 text-[hsl(198,62%,60%)] text-xs rounded-full">
                                      Elegida
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    session.up_image_is === 'B'
                                      ? 'bg-[hsl(160,84%,39%)]/20 text-[hsl(160,84%,50%)]'
                                      : 'bg-[hsl(0,70%,50%)]/20 text-[hsl(0,70%,60%)]'
                                  }`}>
                                    {session.up_image_is === 'B' ? '↑ SUBIDA' : '↓ BAJADA'}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full max-w-[150px] h-24 bg-[hsl(232,20%,14%)] rounded-lg flex items-center justify-center text-white/30">
                                Sin imagen
                              </div>
                            )}
                          </div>
                          
                          {/* Detalles */}
                          <div className="space-y-2">
                            <p><span className="text-white/50">Tipo:</span> <span className="text-white">{session.session_type === 'custom' ? 'Personalizada' : session.session_type === 'advanced' ? 'Avanzada' : 'Ciega'}</span></p>
                            <p><span className="text-white/50">Horizonte:</span> <span className="text-white">{session.horizon_hours}h</span></p>
                            <p><span className="text-white/50">Email:</span> <span className="text-white">{session.user_email}</span></p>
                            <p><span className="text-white/50">Precio base:</span> <span className="text-white">${session.baseline_price?.toFixed(4)}</span></p>
                            <p><span className="text-white/50">Precio final:</span> <span className="text-white">{session.verification_price ? `$${session.verification_price.toFixed(4)}` : 'Pendiente'}</span></p>
                            <p><span className="text-white/50">Confianza:</span> <span className="text-white">{session.confidence || '-'}/5</span></p>
                            <p><span className="text-white/50">Fecha objetivo:</span> <span className="text-white">{new Date(session.target_date_utc).toLocaleString('es-ES')}</span></p>
                            
                            {/* Delete button */}
                            <div className="pt-4 border-t border-[hsl(232,18%,18%)] mt-4">
                              {deleteConfirm === session.id ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-red-400 text-sm">¿Eliminar?</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteSession(session.id); }}
                                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded"
                                  >
                                    Sí
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                                    className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-sm rounded"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(session.id); }}
                                  className="flex items-center gap-2 px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm rounded transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Eliminar sesión
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Gestión de Usuarios</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-white/50 border-b border-[hsl(232,18%,18%)]">
                  <th className="pb-3 font-medium">Usuario</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium">Rol</th>
                  <th className="pb-3 font-medium">Sesión Personal.</th>
                  <th className="pb-3 font-medium">Sesión Avanzada</th>
                  <th className="pb-3 font-medium">Creado</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[hsl(232,18%,18%)] hover:bg-white/5">
                    <td className="py-3 text-white font-medium">{u.name}</td>
                    <td className="py-3 text-white/70">{u.email}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        u.role === 'admin' ? 'bg-[hsl(45,93%,47%)]/20 text-[hsl(45,93%,50%)]' : 'bg-white/10 text-white/70'
                      }`}>
                        {u.role === 'admin' ? 'Admin' : 'Usuario'}
                      </span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleToggleCustomSessions(u.id, u.custom_sessions_enabled)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          u.custom_sessions_enabled
                            ? 'bg-[hsl(160,84%,39%)]/20 text-[hsl(160,84%,50%)] hover:bg-[hsl(160,84%,39%)]/30'
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                      >
                        {u.custom_sessions_enabled ? (
                          <><UserCheck className="w-4 h-4" /> Activada</>
                        ) : (
                          <><UserX className="w-4 h-4" /> Bloqueada</>
                        )}
                      </button>
                    </td>
                    <td className="py-3">
                      {u.advanced_sessions_unlocked ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-[hsl(45,93%,47%)]/20 text-[hsl(45,93%,50%)]">
                          Desbloqueada
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/10 text-white/50">
                          {u.days_until_advanced} días restantes
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-white/50 text-sm">
                      {new Date(u.created_at).toLocaleDateString('es-ES')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 max-w-xl">
          <h2 className="text-xl font-semibold text-white mb-6">Configuración Global</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-white/70 text-sm mb-2">
                Días para desbloquear Sesión Avanzada
              </label>
              <p className="text-white/40 text-xs mb-3">
                Cantidad de días desde la creación de la cuenta para que un usuario pueda acceder a las sesiones avanzadas (30 respiraciones).
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={globalSettings.advanced_session_days}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, advanced_session_days: parseInt(e.target.value) || 0 })}
                  className="w-24 px-3 py-2 bg-[hsl(232,20%,14%)] border border-[hsl(232,18%,18%)] rounded-lg text-white text-center"
                />
                <span className="text-white/50">días</span>
                <button
                  onClick={() => handleUpdateAdvancedDays(globalSettings.advanced_session_days)}
                  className="px-4 py-2 bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] text-white rounded-lg transition-colors"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
